import {
  computeAddonMinutes,
  computePatientRequiredPrepTime,
} from "@/lib/service-utils";

import type { AvailabilityRequestAssignment } from "@/types/self-schedule";
import type {
  PublicServiceAddon,
  PublicServiceDetail,
  ServiceTiny,
} from "@/types/service";

export function getSelfSchedulePriceRange(
  service: PublicServiceDetail | ServiceTiny,
): {
  min: number;
  max: number;
} {
  const hasPromotion = !!service.promotionId || !!service.promotionName;
  const checkoutPrice = "price" in service ? service.price : undefined;

  if (hasPromotion && checkoutPrice != null && checkoutPrice > 0) {
    const percentage = service.promotionDiscountPercentage;
    const discountAmount =
      percentage != null && percentage > 0
        ? Math.floor((checkoutPrice * percentage) / 100)
        : (service.promotionDiscountAmount ?? 0);
    const promotedCheckoutPrice = Math.max(0, checkoutPrice - discountAmount);
    return { min: promotedCheckoutPrice, max: promotedCheckoutPrice };
  }

  return {
    min: service.displayPrice ?? 0,
    max: service.displayPriceMax ?? service.displayPrice ?? 0,
  };
}

/**
 * A core whose display price already includes its add-ons (e.g. sold by
 * area). Once add-ons are picked the display price would count them twice,
 * so the core is priced at what it bills.
 */
export function quotesWithAddons(
  core: { displayPriceIncludesAddons?: boolean },
  pickedAddonCount: number,
): boolean {
  return !!core.displayPriceIncludesAddons && pickedAddonCount > 0;
}

/** The services that carry a price for the appointment, with their ranges. */
function pricedLines(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): { service: PublicServiceDetail | ServiceTiny; min: number; max: number }[] {
  return resolveServiceOccurrences(services, selectedAddons).flatMap(
    ({ service, addons }) => [
      service.price != null && quotesWithAddons(service, addons.length)
        ? {
            // Billed as-is: the range's flags no longer describe the price.
            service: {
              ...service,
              displayPriceMax: undefined,
              priceStartsAt: false,
              estimated: false,
            },
            min: service.price,
            max: service.price,
          }
        : { service, ...getSelfSchedulePriceRange(service) },
      ...addons.map((addon) => ({
        service: addon.addonService as ServiceTiny,
        ...getSelfSchedulePriceRange(addon.addonService as ServiceTiny),
      })),
    ],
  );
}

/** Key one occurrence of a service within an appointment (1-based). */
export function occurrenceKey(
  serviceId: string,
  occurrenceIndex: number,
): string {
  return `${serviceId}:${occurrenceIndex}`;
}

function addonIdsForOccurrence(
  selectedAddons: Record<string, string[]>,
  serviceId: string,
  occurrenceIndex: number,
): string[] {
  const occurrenceIds =
    selectedAddons[occurrenceKey(serviceId, occurrenceIndex)];
  if (occurrenceIds) return occurrenceIds;
  // A legacy service-id key predates duplicate occurrences, so the server reads
  // it as occurrence 1. Later occurrences must not inherit it.
  return occurrenceIndex === 1 ? (selectedAddons[serviceId] ?? []) : [];
}

export interface ServiceOccurrence {
  service: PublicServiceDetail;
  /** 1-based position among occurrences of the same service. */
  occurrenceIndex: number;
  /** The key this occurrence's add-on selections are stored under. */
  occurrenceKey: string;
  /** Selected add-on mapping IDs, in the order the patient picked them. */
  addonIds: string[];
  /** The selected mappings themselves, aligned with `addonIds`. */
  addons: PublicServiceAddon[];
}

/**
 * The one place an appointment's flat service list is turned into occurrences.
 *
 * Duplicate services (e.g. "2 × Small area") each get their own occurrence, and
 * add-on selections may be keyed by occurrence as `${serviceId}:${index}` with
 * legacy service-id keys still accepted for single-instance selections.
 * Everything that needs to align services, occurrences, and add-ons reads this
 * rather than re-deriving the walk.
 */
export function resolveServiceOccurrences(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): ServiceOccurrence[] {
  const occurrenceCounts = new Map<string, number>();

  return services.map((service) => {
    const occurrenceIndex = (occurrenceCounts.get(service.id) ?? 0) + 1;
    occurrenceCounts.set(service.id, occurrenceIndex);
    const addonIds = addonIdsForOccurrence(
      selectedAddons,
      service.id,
      occurrenceIndex,
    );

    return {
      service,
      occurrenceIndex,
      occurrenceKey: occurrenceKey(service.id, occurrenceIndex),
      addonIds,
      // Selection order, not configured order — this is the order the booking
      // request sends, so display and payload cannot disagree.
      addons: addonIds
        .map((addonId) =>
          service.addons?.find((candidate) => candidate.id === addonId),
        )
        .filter((addon): addon is PublicServiceAddon => addon !== undefined),
    };
  });
}

/**
 * Flatten parent + addon service IDs for availability/booking queries.
 *
 * Duplicate parent services (e.g. "2 × Small area") repeat — the backend
 * creates one EventService per parent entry. Add-on selections may be keyed
 * by occurrence as `${serviceId}:${occurrenceIndex}` (1-based), with legacy
 * service-id keys still accepted for single-instance selections.
 */
export function flattenServiceIds(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): string[] {
  return resolveServiceOccurrences(services, selectedAddons).flatMap(
    ({ service, addons }) => [
      service.id,
      ...addons.map((addon) => addon.addonService.id),
    ],
  );
}

/** Repeated add-on mappings get an ordinal suffix; the first keeps the plain key. */
export function addonEntryKey(
  instanceId: string,
  mappingId: string,
  ordinal: number,
): string {
  const key = `${instanceId}/addon/${mappingId}`;
  return ordinal > 1 ? `${key}#${ordinal}` : key;
}

/** Entry keys for one instance's add-on selections, in selection order. */
export function addonEntryKeys(
  instanceId: string,
  mappingIds: readonly string[],
): string[] {
  const counts = new Map<string, number>();
  return mappingIds.map((mappingId) => {
    const ordinal = (counts.get(mappingId) ?? 0) + 1;
    counts.set(mappingId, ordinal);
    return addonEntryKey(instanceId, mappingId, ordinal);
  });
}

export interface FlattenedServiceAssignmentEntry {
  assignment: AvailabilityRequestAssignment;
  sourceInstanceId: string;
  entryKey: string;
}

/** Build ordered service/provider pairs while retaining their parent instance. */
export function flattenServiceAssignmentEntries(
  instances: readonly { id: string; instanceId: string }[],
  serviceById: ReadonlyMap<string, PublicServiceDetail>,
  selectedAddons: Record<string, string[]>,
  serviceInstanceProviderIds: Record<string, string>,
): FlattenedServiceAssignmentEntry[] {
  // Instances the front page can't resolve to a service are dropped, so keep
  // each occurrence paired with the instance it came from.
  const resolvable = instances.flatMap((instance) => {
    const service = serviceById.get(instance.id);
    return service ? [{ instance, service }] : [];
  });
  const occurrences = resolveServiceOccurrences(
    resolvable.map(({ service }) => service),
    selectedAddons,
  );

  return occurrences.flatMap(({ service, addons }, index) => {
    const { instanceId } = resolvable[index].instance;
    const addonKeys = addonEntryKeys(
      instanceId,
      addons.map((addon) => addon.id),
    );
    return [
      {
        assignment: {
          serviceId: service.id,
          providerId: serviceInstanceProviderIds[instanceId] ?? null,
        },
        sourceInstanceId: instanceId,
        entryKey: instanceId,
      },
      ...addons.map((addon, addonIndex) => ({
        assignment: { serviceId: addon.addonService.id, providerId: null },
        sourceInstanceId: instanceId,
        entryKey: addonKeys[addonIndex],
      })),
    ];
  });
}

/** Build ordered service/provider pairs without parallel arrays. */
export function flattenServiceAssignments(
  instances: readonly { id: string; instanceId: string }[],
  serviceById: ReadonlyMap<string, PublicServiceDetail>,
  selectedAddons: Record<string, string[]>,
  serviceInstanceProviderIds: Record<string, string>,
): AvailabilityRequestAssignment[] {
  return flattenServiceAssignmentEntries(
    instances,
    serviceById,
    selectedAddons,
    serviceInstanceProviderIds,
  ).map(({ assignment }) => assignment);
}

/** Flatten selected parent and add-on services in appointment order. */
export function flattenSelectedServices(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): ServiceTiny[] {
  return resolveServiceOccurrences(services, selectedAddons).flatMap(
    ({ service, addons }) => [
      service as ServiceTiny,
      ...addons.map((addon) => addon.addonService),
    ],
  );
}

/**
 * Compute the patient-facing appointment duration, including selected add-ons
 * and consolidated prep/buffer time when the patient is required for prep.
 */
export function computeTotalDuration(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): number {
  const serviceMinutes = resolveServiceOccurrences(
    services,
    selectedAddons,
  ).reduce(
    (total, { service, addons }) =>
      total +
      service.duration +
      computeAddonMinutes(
        service,
        addons.map((addon) => addon.addonService.duration),
      ),
    0,
  );
  return (
    serviceMinutes +
    computePatientRequiredPrepTime(
      flattenSelectedServices(services, selectedAddons),
    )
  );
}

/** Compute total display price including selected addons. */
export function computeTotalPrice(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): number {
  return pricedLines(services, selectedAddons).reduce(
    (total, line) => total + line.min,
    0,
  );
}

/** Compute total price range (min/max) including selected addons. */
export function computeTotalPriceRange(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): { min: number; max: number } {
  return pricedLines(services, selectedAddons).reduce(
    (total, { min, max }) => ({ min: total.min + min, max: total.max + max }),
    { min: 0, max: 0 },
  );
}

/** Check if any service or selected addon has variable pricing. */
export function hasAnyPriceRange(
  services: PublicServiceDetail[],
  selectedAddons: Record<string, string[]>,
): { isRange: boolean; isStartsAt: boolean; isEstimated: boolean } {
  let isRange = false;
  let isStartsAt = false;
  let isEstimated = false;

  for (const { service } of pricedLines(services, selectedAddons)) {
    if (
      service.displayPriceMax &&
      service.displayPriceMax > (service.displayPrice ?? 0)
    )
      isRange = true;
    if (service.priceStartsAt) isStartsAt = true;
    if (service.estimated) isEstimated = true;
  }

  return { isRange, isStartsAt, isEstimated };
}
